import {ReactNode} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {SxProps, Theme} from '@mui/material/styles';
import Card from './Card';
import StatusChip from './StatusChip';
import {breakpoint, fonts, kit, radius} from './tokens';
import {RfpStatusInput, StatusTone} from './rfpStatus';

interface HeroStatusCardProps {
  // Icon-tile + title + meta + status-chip row. Omit `title` for the "solo"
  // greeting variant (e.g. a dashboard hero with no project identity above
  // the headline) -- the whole head row and the tracker slot disappear and
  // the headline loses its top margin, matching `.hrow.solo` in the mockup.
  icon?: ReactNode;
  title?: string;
  // Meta row under the title (icon + text), e.g. "Kitchener, ON · Posted 3
  // days ago" -- caller-composed since its shape varies per screen.
  meta?: ReactNode;
  // Status chip -- forwarded straight to the kit StatusChip (same trio of
  // props), so pass either a status-shaped object or a direct label/tone.
  status?: RfpStatusInput | null;
  statusLabel?: string;
  statusTone?: StatusTone;
  // LifecycleTracker element, rendered between the head and the headline.
  tracker?: ReactNode;
  // Serif status headline (~31px) -- always shown.
  headline: ReactNode;
  subline?: ReactNode;
  // Single primary action (button/link) -- exactly one blue primary per the
  // kit rule. Beside the text on desktop, drops full-width beneath it on
  // phone, natural-width beneath it on tablet.
  action?: ReactNode;
  sx?: SxProps<Theme>;
}

// The card at the top of every project/job detail screen: icon tile + title
// + meta + status chip, an optional lifecycle tracker, then a serif headline
// + subline + one action. Web port of `.card.hero` in
// web-detail-1-quotes.html.
const HeroStatusCard = ({
  icon,
  title,
  meta,
  status,
  statusLabel,
  statusTone,
  tracker,
  headline,
  subline,
  action,
  sx,
}: HeroStatusCardProps) => {
  const hasHead = Boolean(title);

  return (
    <Card
      radius="lg"
      sx={[
        {
          padding: '24px 26px 26px',
          boxShadow:
            '0 10px 30px rgba(19,33,46,.07), 0 2px 6px rgba(19,33,46,.04)',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}>
      {hasHead ? (
        <Box sx={{display: 'flex', alignItems: 'center', gap: '14px'}}>
          {icon ? (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: `${radius.md}px`,
                background: `linear-gradient(135deg, ${kit.blueTint}, ${kit.purpleTint})`,
                color: kit.blueInk,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
              {icon}
            </Box>
          ) : null}
          <Box sx={{flex: 1, minWidth: 0}}>
            <Typography
              sx={{
                fontSize: 17.5,
                fontWeight: 700,
                letterSpacing: '-0.2px',
                color: kit.ink,
              }}>
              {title}
            </Typography>
            {meta ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginTop: '4px',
                  fontSize: 13,
                  fontWeight: 500,
                  color: kit.slate,
                }}>
                {meta}
              </Box>
            ) : null}
          </Box>
          <Box sx={{flexShrink: 0}}>
            <StatusChip status={status} label={statusLabel} tone={statusTone} />
          </Box>
        </Box>
      ) : null}

      {tracker}

      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '20px',
          marginTop: hasHead || tracker ? '18px' : 0,
          [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '32px',
          },
        })}>
        <Box sx={{minWidth: 0}}>
          <Typography
            sx={{
              fontFamily: fonts.serif,
              fontSize: 31,
              lineHeight: 1.16,
              letterSpacing: '-0.3px',
              color: kit.ink,
              fontWeight: 400,
            }}>
            {headline}
          </Typography>
          {subline ? (
            <Typography
              sx={{
                fontSize: 15,
                lineHeight: 1.5,
                color: kit.slate,
                fontWeight: 500,
                marginTop: '8px',
              }}>
              {subline}
            </Typography>
          ) : null}
        </Box>
        {action ? (
          <Box
            sx={(theme) => ({
              flexShrink: 0,
              width: '100%',
              '& > *': {width: '100%'},
              [theme.breakpoints.up(breakpoint.tablet)]: {
                width: 'auto',
                '& > *': {width: 'auto'},
              },
            })}>
            {action}
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};

export default HeroStatusCard;
